import { ReactComponent as WarningStaticIcon } from '@basqet-dashboard/common/assets/icons/warning-static.svg';
import sampleIdDocument from '@basqet-dashboard/common/assets/images/sample-id-document.png';
import { Button, FormInput, Modal } from '@basqet-dashboard/common/components';
import { flexibleUrl } from '@basqet-dashboard/common/utils/url-validator';
import { FileUpload } from '@components/onboarding';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { getRemoteFileByAttachmentField, mapAttachmentMetaByField } from '@utils/attachment-meta';
import { mapFreelancerAdditionalInfo } from '@utils/compliance-mappers';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { KYB_SAMPLE_DOCS } from '../../shared-constants';

const additionalInfoSchema = yup.object({
  platform_link: flexibleUrl(yup.string()).required('Platform link is required')
});

const FILE_FIELDS = {
  IDENTIFICATION_DOCUMENT: 'identification_document',
  PROOF_OF_ADDRESS: 'proof_of_address',
  PLATFORM_EVIDENCE: 'platform_evidence'
};

const ATTACHMENT_FIELD_BY_FILE_FIELD = {
  [FILE_FIELDS.IDENTIFICATION_DOCUMENT]: 'means_of_identification_attachment_id',
  [FILE_FIELDS.PROOF_OF_ADDRESS]: 'proof_of_address_attachment_id',
  [FILE_FIELDS.PLATFORM_EVIDENCE]: 'platform_evidence_attachment_id'
};

const AdditionalInformation = ({ onComplete, initialData, readOnly = false }) => {
  const { track } = useAnalytics();
  const { uploadFile, saveSection, submitForReview } = useCompliance();
  const attachmentMetaByField = mapAttachmentMetaByField(initialData?.attachment_meta);

  useTrackOnMount(ANALYTICS_EVENTS.KYB_DOCUMENT_PAGE_VIEWED, {
    kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER
  });

  const [files, setFiles] = useState({
    [FILE_FIELDS.IDENTIFICATION_DOCUMENT]: getRemoteFileByAttachmentField(
      attachmentMetaByField,
      ATTACHMENT_FIELD_BY_FILE_FIELD[FILE_FIELDS.IDENTIFICATION_DOCUMENT]
    ),
    [FILE_FIELDS.PROOF_OF_ADDRESS]: getRemoteFileByAttachmentField(
      attachmentMetaByField,
      ATTACHMENT_FIELD_BY_FILE_FIELD[FILE_FIELDS.PROOF_OF_ADDRESS]
    ),
    [FILE_FIELDS.PLATFORM_EVIDENCE]: getRemoteFileByAttachmentField(
      attachmentMetaByField,
      ATTACHMENT_FIELD_BY_FILE_FIELD[FILE_FIELDS.PLATFORM_EVIDENCE]
    )
  });
  const [attachmentIds, setAttachmentIds] = useState({
    [FILE_FIELDS.IDENTIFICATION_DOCUMENT]:
      initialData?.means_of_identification_attachment_id || null,
    [FILE_FIELDS.PROOF_OF_ADDRESS]: initialData?.proof_of_address_attachment_id || null,
    [FILE_FIELDS.PLATFORM_EVIDENCE]: initialData?.platform_evidence_attachment_id || null
  });
  const [fileErrors, setFileErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(initialData?.confirmed || false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(additionalInfoSchema),
    defaultValues: { platform_link: initialData?.platform_link || '' }
  });

  const handleFileChange = async (field, file) => {
    if (!file) return;

    setFiles((prev) => ({ ...prev, [field]: file }));
    setFileErrors((prev) => ({ ...prev, [field]: undefined }));

    try {
      track(ANALYTICS_EVENTS.KYB_DOC_UPLOADED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER,
        doc_type: field
      });

      const result = await uploadFile(file);
      setAttachmentIds((prev) => ({ ...prev, [field]: result.attachment_id }));
    } catch (error) {
      setFiles((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleDeleteRequest = (field) => {
    setDeleteTarget(field);
    setShowDeleteModal(true);
  };

  const validateFiles = () => {
    const errs = {};
    if (!attachmentIds[FILE_FIELDS.IDENTIFICATION_DOCUMENT])
      errs[FILE_FIELDS.IDENTIFICATION_DOCUMENT] = 'Identification document is required';
    if (!attachmentIds[FILE_FIELDS.PROOF_OF_ADDRESS])
      errs[FILE_FIELDS.PROOF_OF_ADDRESS] = 'Proof of address is required';
    setFileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const confirmDelete = () => {
    track(ANALYTICS_EVENTS.KYB_DOC_DELETED, {
      kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER,
      doc_type: deleteTarget
    });
    setFiles((prev) => ({ ...prev, [deleteTarget]: null }));
    setAttachmentIds((prev) => ({ ...prev, [deleteTarget]: null }));
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const onSubmit = async (data) => {
    if (!validateFiles()) return;

    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.KYB_SUBMIT_CLICKED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER
      });
      const apiData = mapFreelancerAdditionalInfo(data, attachmentIds);
      const result = await saveSection('FREELANCER_ADDITIONAL_INFO', apiData, {
        entryId: initialData?._entryId,
        version: initialData?._version
      });

      await submitForReview();
      onComplete({
        ...data,
        ...attachmentIds,
        _entryId: result.entry_id,
        _version: result.version
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const requiredFilesFilled =
    !!files[FILE_FIELDS.IDENTIFICATION_DOCUMENT] && !!files[FILE_FIELDS.PROOF_OF_ADDRESS];

  return (
    <form className="additional-information" onSubmit={handleSubmit(onSubmit)}>
      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div className="additional-information__field">
          <FileUpload
            label="Means of Identification"
            disabled={readOnly}
            description="Please upload a clear picture of your valid govt-issued ID (Passport, Driver's license, National ID card/NIN slip)"
            value={files[FILE_FIELDS.IDENTIFICATION_DOCUMENT]}
            onChange={(file) => handleFileChange(FILE_FIELDS.IDENTIFICATION_DOCUMENT, file)}
            onDelete={() => handleDeleteRequest(FILE_FIELDS.IDENTIFICATION_DOCUMENT)}
            onView={() =>
              track(ANALYTICS_EVENTS.KYB_DOC_VIEWED, {
                kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER,
                doc_type: FILE_FIELDS.IDENTIFICATION_DOCUMENT
              })
            }
            sampleTitle={{ normal: 'Means of', cursive: 'Identification' }}
            sampleSubtitle="Sample Document"
            sampleImage={sampleIdDocument}
            maxSizeMB={5}
            error={fileErrors[FILE_FIELDS.IDENTIFICATION_DOCUMENT]}
          />
        </div>

        <div className="additional-information__field">
          <FileUpload
            label="Proof of Address"
            description="Please note that the proof of address required is your utility bill or bank statement no older than 3 months"
            value={files[FILE_FIELDS.PROOF_OF_ADDRESS]}
            onChange={(file) => handleFileChange(FILE_FIELDS.PROOF_OF_ADDRESS, file)}
            onDelete={() => handleDeleteRequest(FILE_FIELDS.PROOF_OF_ADDRESS)}
            onView={() =>
              track(ANALYTICS_EVENTS.KYB_DOC_VIEWED, {
                kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER,
                doc_type: FILE_FIELDS.PROOF_OF_ADDRESS
              })
            }
            sampleTitle={{ normal: 'Sample', cursive: 'Proof of Address' }}
            sampleImage={KYB_SAMPLE_DOCS.PROOF_OF_ADDRESS}
            maxSizeMB={5}
            error={fileErrors[FILE_FIELDS.PROOF_OF_ADDRESS]}
            disabled={readOnly}
          />
        </div>

        <div className="additional-information__field">
          <h3 className="additional-information__field-title">Platform Link</h3>
          <p className="additional-information__field-description">
            Share a link to your main business profile (website, social media, LinkedIn, Fiverr
            etc). The profile must be public.
          </p>
          <FormInput
            name="platform_link"
            type="text"
            placeholder="https://freelanceplatformlink.url"
            register={register('platform_link')}
            error={errors.platform_link?.message}
          />
        </div>

        <div className="additional-information__field">
          <FileUpload
            label="Platform Evidence"
            description="Upload a clear screenshot of your profile page on the same platform showing your bio data. The profile must be public."
            value={files[FILE_FIELDS.PLATFORM_EVIDENCE]}
            onChange={(file) => handleFileChange(FILE_FIELDS.PLATFORM_EVIDENCE, file)}
            onDelete={() => handleDeleteRequest(FILE_FIELDS.PLATFORM_EVIDENCE)}
            onView={() =>
              track(ANALYTICS_EVENTS.KYB_DOC_VIEWED, {
                kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER,
                doc_type: FILE_FIELDS.PLATFORM_EVIDENCE
              })
            }
            sampleTitle={{ normal: 'Platform', cursive: 'Evidence' }}
            maxSizeMB={5}
            disabled={readOnly}
          />
        </div>
      </fieldset>

      {!readOnly && (
        <>
          <label className="additional-information__confirm-label">
            <input
              type="checkbox"
              className="additional-information__confirm-checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I confirm that all information and documents provided are accurate. Incorrect or
            incomplete details may delay the review or lead to rejection. Compliance may contact me
            if clarification is needed.
          </label>

          <Button
            type="submit"
            isFull
            isLoading={isSubmitting}
            disabled={!requiredFilesFilled || !confirmed}>
            Save and Continue
          </Button>
        </>
      )}

      {showDeleteModal && (
        <Modal
          Icon={WarningStaticIcon}
          header={{ name: 'Delete', cursive: 'Document' }}
          onModalClose={() => setShowDeleteModal(false)}>
          <p className="modal__text">Are you sure you want to delete this document?</p>
          <Button isFull variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button isFull onClick={confirmDelete}>
            Yes, continue
          </Button>
        </Modal>
      )}
    </form>
  );
};

AdditionalInformation.propTypes = {
  onComplete: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  readOnly: PropTypes.bool
};

export { AdditionalInformation };
